import { FC, useCallback, useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { useBreakpoint } from '@contexts/breakpoints';
import { callService } from '@api/caller';
import { VehicleOfficeSearchViewModel } from '@api/index';
import { FlexibleDiv } from '@components/formStyles';
import {
  ItemText,
  ItemTextWrapper,
} from '@components/ui/search/SearchResultListStyle';
import CustomIcon from '@components/utils/CustomIcon';
import { getLocationIcon } from '@components/ui/search/SearchLocation/utils/getLocationIcon';
import AutoComplete, { Option } from '@shared/AutoComplete';
import { MapPin2Line } from '@assets/icons';
import { PastSearches } from '@shared';
import Drawer from '@components/Drawer';
import ResponsiveApplyButton from '@components/views/components/listing/drawer/ResponsiveApplyButton/ResponsiveApplyButton';
import { CarRentalLocationType } from '@contexts/searchbar/types/car';

import {
  Arrow,
  ErrorMessage,
  ErrorMessageContainer,
  ItemErrorText,
  OptionWrapper,
  SelectWrapper,
  InputWrapper,
} from './styled';

type PropsTypes = {
  label: string;
  onChange?: (airport: CarRentalLocationType) => void;
  error?: boolean;
  activeLocation?: CarRentalLocationType;
  history?: VehicleOfficeSearchViewModel[];
  clearPastFlightSearch?: () => void;
  removePastFlightSearch?: (item: VehicleOfficeSearchViewModel) => void;
  isLocationError?: boolean;
  setIsLocationError?: (value: boolean) => void;
};

const CarRentalAutoComplate: FC<PropsTypes> = ({
  label,
  onChange: pOnChange = (v) => v,
  activeLocation,
  history,
  clearPastFlightSearch,
  removePastFlightSearch,
  isLocationError,
  setIsLocationError,
}) => {
  const { xs, xl } = useBreakpoint();

  const [inputValue, setInputValue] = useState('');
  const [locations, setLocations] = useState<
    Array<VehicleOfficeSearchViewModel> | []
  >([]);
  const [isFocus, setIsFocus] = useState(false);
  const [isLocationsLoading, setIsLocationsLoading] = useState(false);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const [isHeaderOpen, setIsHeaderOpen] = useState<boolean>(false);

  const handleSearch = (value: string) => {
    setInputValue(value);

    if (value === '') {
      pOnChange(null);
    }

    setIsDataFetched(false);
  };
  useEffect(() => {
    if (activeLocation) {
      pOnChange(activeLocation as CarRentalLocationType);
      setInputValue(activeLocation?.text || '');
    }
  }, [activeLocation]);

  const handleFocus = () => {
    setIsFocus(true);
    isLocationError && setIsLocationError?.(false);
  };

  const handleBlur = () => {
    setIsFocus(false);
    isLocationError && setIsLocationError?.(false);
  };

  const fetchData = async (s: string) => {
    try {
      setIsLocationsLoading(true);
      const data = await callService('SearchesService', 'searchVehicle', {
        s: s,
      });
      setLocations(data);
    } catch (error) {
      setLocations([]);
    } finally {
      setIsLocationsLoading(false);
      setIsDataFetched(true);
    }
  };

  const searchAirport = useCallback(
    debounce((s: string) => fetchData(s), 500),
    []
  );

  useEffect(() => {
    if (inputValue) {
      const isDifferentValue = activeLocation?.text !== inputValue;
      if (inputValue.length > 2 && isDifferentValue) searchAirport(inputValue);
    } else {
      setLocations([]);
    }
  }, [inputValue]);

  const selectValue = (option: CarRentalLocationType) => {
    pOnChange(option);
    setInputValue(option?.text || '');
  };

  const getBoldText = (keyword: string) => '<b>' + keyword + '</b>';

  const makeBold = (item: string, keyword: string) => {
    return item.replace(keyword, getBoldText(keyword));
  };

  const isShowItemTextError =
    isDataFetched &&
    !isLocationsLoading &&
    inputValue.length > 2 &&
    locations.length <= 0;

  const showPastSearch = Array.isArray(history)
    ? history?.length > 0 && inputValue.length === 0
    : false;
  const handleHeaderOpen = () => setIsHeaderOpen((oldState) => !oldState);

  return (
    <SelectWrapper>
      {!xl ? (
        <>
          <InputWrapper onClick={handleHeaderOpen}>
            <AutoComplete
              onSearch={handleSearch}
              onFocus={handleFocus}
              onBlur={handleBlur}
              allowClear
              isFocus={isFocus}
              isError={isLocationError}
              value={inputValue}
              dropdownStyle={{
                minWidth: xs ? 'auto' : '400px',
              }}
              label={label}
              icon={MapPin2Line}
            />
          </InputWrapper>
          <Drawer
            title={label}
            open={isHeaderOpen}
            onClose={handleHeaderOpen}
            placement='top'
            footer={
              <ResponsiveApplyButton
                title='Uygula'
                onClick={handleHeaderOpen}
              />
            }
          >
            <SelectWrapper isHeaderOpen={isHeaderOpen}>
              <AutoComplete
                onSearch={handleSearch}
                onFocus={handleFocus}
                onBlur={handleBlur}
                allowClear
                isFocus={isFocus}
                isError={isLocationError as boolean}
                value={inputValue}
                dropdownStyle={{
                  minWidth: xs ? 'auto' : '400px',
                }}
                label={label}
                icon={MapPin2Line}
              >
                <>
                  {showPastSearch ? (
                    <>
                      <Option className='pastSearchesTitleWrapper'>
                        <PastSearches.Header
                          label='Son Aramalar'
                          handleClear={() => clearPastFlightSearch?.()}
                        />
                      </Option>
                      {history?.map((item) => (
                        <Option key={item?.slug}>
                          <PastSearches.Item
                            label={item.text}
                            onClick={() => selectValue(item)}
                            type={item?.type}
                            handleRemove={() => removePastFlightSearch?.(item)}
                          />
                        </Option>
                      ))}
                    </>
                  ) : null}
                  {locations?.length ? (
                    locations?.map((option, index) => (
                      <Option key={`${option.code}-${index}`}>
                        <OptionWrapper onClick={() => selectValue(option)}>
                          <CustomIcon
                            fontSize={24}
                            paletteColor='primaryBrand'
                            icon={getLocationIcon(option?.type || '')}
                          />
                          <FlexibleDiv right={17} />
                          <ItemTextWrapper>
                            <ItemText
                              dangerouslySetInnerHTML={{
                                __html: makeBold(`${option.text}`, inputValue),
                              }}
                            />
                          </ItemTextWrapper>
                        </OptionWrapper>
                      </Option>
                    ))
                  ) : isShowItemTextError ? (
                    <Option>
                      <OptionWrapper>
                        <ItemErrorText>
                          {!xs ? (
                            <span>
                              Aradığınız kriterlere uygun sonuç bulunamadı.
                            </span>
                          ) : (
                            <span>
                              Aradığınız kriterlere uygun <br />
                              sonuç bulunamadı.
                            </span>
                          )}
                        </ItemErrorText>
                      </OptionWrapper>
                    </Option>
                  ) : null}
                </>
              </AutoComplete>
            </SelectWrapper>
          </Drawer>
        </>
      ) : (
        <SelectWrapper isHeaderOpen={isHeaderOpen}>
          <AutoComplete
            onSearch={handleSearch}
            onFocus={handleFocus}
            onBlur={handleBlur}
            allowClear
            isFocus={isFocus}
            isError={isLocationError}
            value={inputValue}
            dropdownStyle={{
              minWidth: xs ? 'auto' : '400px',
            }}
            label={label}
            icon={MapPin2Line}
          >
            <>
              {showPastSearch ? (
                <>
                  <Option className='pastSearchesTitleWrapper'>
                    <PastSearches.Header
                      label='Son Aramalar'
                      handleClear={() => clearPastFlightSearch?.()}
                    />
                  </Option>
                  {history?.map((item) => (
                    <Option key={item?.slug}>
                      <PastSearches.Item
                        label={item.text}
                        onClick={() => selectValue(item)}
                        type={getLocationIcon(item?.type || '')}
                        handleRemove={() => removePastFlightSearch?.(item)}
                      />
                    </Option>
                  ))}
                </>
              ) : null}
              {locations.length ? (
                locations.map((option, index) => (
                  <Option key={`${option.code}-${index}`}>
                    <OptionWrapper onClick={() => selectValue(option)}>
                      <CustomIcon
                        fontSize={24}
                        paletteColor='primaryBrand'
                        icon={getLocationIcon(option?.type || '')}
                      />
                      <FlexibleDiv right={17} />
                      <ItemTextWrapper>
                        <ItemText
                          dangerouslySetInnerHTML={{
                            __html: makeBold(`${option.text}`, inputValue),
                          }}
                        />
                      </ItemTextWrapper>
                    </OptionWrapper>
                  </Option>
                ))
              ) : isShowItemTextError ? (
                <Option>
                  <OptionWrapper>
                    <ItemErrorText>
                      {!xs ? (
                        <span>
                          Aradığınız kriterlere uygun sonuç bulunamadı.
                        </span>
                      ) : (
                        <span>
                          Aradığınız kriterlere uygun <br />
                          sonuç bulunamadı.
                        </span>
                      )}
                    </ItemErrorText>
                  </OptionWrapper>
                </Option>
              ) : null}
            </>
          </AutoComplete>
        </SelectWrapper>
      )}

      {isLocationError && (
        <ErrorMessageContainer>
          <Arrow />
          <ErrorMessage>Şehir Adını Kontrol Ediniz</ErrorMessage>
        </ErrorMessageContainer>
      )}
    </SelectWrapper>
  );
};

export default CarRentalAutoComplate;
